import Field from '../Field.jsx';
import FieldGroup from '../FieldGroup.jsx';
import { Toggle } from './primitives.jsx';

const TIER_KEYS = ['free', 'pro'];
const TIER_LABEL = { free: 'Free', pro: 'Pro' };

export default function AgenticSection({ aiAgents, agenticInfra, set }) {
  const setA = (field, value) => set(`aiAgents.${field}`, value);
  const setI = (field, value) => set(`agenticInfra.${field}`, value);

  return (
    <div className="space-y-5">
      <section className="rounded-box border border-carbon-100 bg-white overflow-hidden">
        <header className="px-4 py-3 flex items-center justify-between gap-3">
          <div>
            <p className="label text-carbon-800">Consumer AI economics</p>
            <p className="caption text-carbon-400 mt-1">
              Off in the base case; enable for upside scenarios.
            </p>
          </div>
          <Toggle
            checked={aiAgents.enabled}
            onChange={(v) => setA('enabled', v)}
            ariaLabel="Toggle consumer AI economics"
          />
        </header>
      </section>

      <FieldGroup title="AI agents · per-tier" caption="Runs included plus per-run overage rate">
        {TIER_KEYS.map((tk) => (
          <Field
            key={`ri-${tk}`}
            label={`${TIER_LABEL[tk]} runs included / mo`}
            value={aiAgents.runsIncluded[tk]}
            step={5}
            onChange={(v) => setA(`runsIncluded.${tk}`, v)}
          />
        ))}
        {TIER_KEYS.map((tk) => (
          <Field
            key={`or-${tk}`}
            label={`${TIER_LABEL[tk]} overage ($/run)`}
            value={aiAgents.overageRate[tk]}
            step={0.01}
            decimals={3}
            onChange={(v) => setA(`overageRate.${tk}`, v)}
          />
        ))}
      </FieldGroup>

      <FieldGroup
        title="Outcome-based savings"
        caption="Pulsar Save: % of agentic users opting in × avg savings × Pulsar's cut"
      >
        <Field
          label="Adoption"
          value={aiAgents.outcomeBasedSavings.adoption}
          pct
          step={0.01}
          onChange={(v) => setA('outcomeBasedSavings.adoption', v)}
        />
        <Field
          label="Avg savings / yr ($)"
          value={aiAgents.outcomeBasedSavings.avgPerYear}
          step={20}
          onChange={(v) => setA('outcomeBasedSavings.avgPerYear', v)}
        />
        <Field
          label="Pulsar share"
          value={aiAgents.outcomeBasedSavings.pulsarShare}
          pct
          step={0.01}
          onChange={(v) => setA('outcomeBasedSavings.pulsarShare', v)}
        />
      </FieldGroup>

      <FieldGroup
        title="Merchant referral"
        caption="y1Active is locked false; Y2 onward steady-state"
      >
        <Field
          label="Y2 volume / mo ($)"
          value={aiAgents.merchantReferral.y2Volume}
          step={1000}
          onChange={(v) => setA('merchantReferral.y2Volume', v)}
        />
        <Field
          label="Referral rate"
          value={aiAgents.merchantReferral.refRate}
          pct
          step={0.001}
          onChange={(v) => setA('merchantReferral.refRate', v)}
        />
      </FieldGroup>

      <FieldGroup title="General">
        <Field
          label="Cost per run ($)"
          value={aiAgents.costPerRun}
          step={0.005}
          decimals={3}
          onChange={(v) => setA('costPerRun', v)}
        />
      </FieldGroup>

      <AgenticInfraBlock infra={agenticInfra} setI={setI} />
    </div>
  );
}

function AgenticInfraBlock({ infra, setI }) {
  return (
    <section className="rounded-box border border-carbon-100 bg-white overflow-hidden">
      <header className="px-4 py-3 border-b border-carbon-100 flex items-center justify-between gap-3">
        <div>
          <p className="label text-carbon-800">Agentic Infra (B2B)</p>
          <p className="caption text-carbon-400 mt-1">
            Off by default. Toggle on to add Portal / Settlement / Streaming / Marketplace revenue
            lines.
          </p>
        </div>
        <Toggle
          checked={infra.enabled}
          onChange={(v) => setI('enabled', v)}
          ariaLabel="Toggle agentic infra revenue lines"
        />
      </header>

      {infra.enabled && (
        <div className="p-4 grid gap-4 lg:grid-cols-2">
          <InfraLineCard
            title="Portal upgrade"
            sharePct={70}
            line={infra.portalUpgradeFee}
            volLabel="Active businesses"
            volStep={5}
            volField="activeBusinesses"
            rateLabel="Monthly fee / business ($)"
            rateStep={50}
            rateField="monthlyFeePerBusiness"
            setLine={(field, value) => setI(`portalUpgradeFee.${field}`, value)}
          />
          <InfraLineCard
            title="Settlement"
            sharePct={30}
            line={infra.settlementTxFee}
            volLabel="Volume / mo ($)"
            volStep={50000}
            volField="volumeMonthly"
            rateLabel="Rate / tx"
            rateStep={0.0005}
            rateField="ratePerTx"
            ratePct
            setLine={(field, value) => setI(`settlementTxFee.${field}`, value)}
          />
          <InfraLineCard
            title="Payment streaming"
            sharePct={50}
            line={infra.paymentStreamingFee}
            volLabel="Volume / mo ($)"
            volStep={50000}
            volField="volumeMonthly"
            rateLabel="Rate"
            rateStep={0.0005}
            rateField="rate"
            ratePct
            setLine={(field, value) => setI(`paymentStreamingFee.${field}`, value)}
          />
          <InfraLineCard
            title="Marketplace"
            sharePct={60}
            line={infra.marketplaceFee}
            volLabel="Volume / mo ($)"
            volStep={10000}
            volField="volumeMonthly"
            rateLabel="Rate"
            rateStep={0.005}
            rateField="rate"
            ratePct
            setLine={(field, value) => setI(`marketplaceFee.${field}`, value)}
          />
        </div>
      )}
    </section>
  );
}

function InfraLineCard({
  title,
  sharePct,
  line,
  volLabel,
  volStep,
  volField,
  rateLabel,
  rateStep,
  rateField,
  ratePct = false,
  setLine,
}) {
  return (
    <div className="rounded-box border border-carbon-100 p-4 bg-paper">
      <div className="flex items-baseline justify-between mb-3">
        <p className="label text-carbon-800">{title}</p>
        <span className="label num-tabular px-2 py-0.5 rounded-full bg-mint-100 text-mint-800">
          Pulsar {sharePct}%
        </span>
      </div>
      <Field
        label={volLabel}
        value={line[volField]}
        step={volStep}
        onChange={(v) => setLine(volField, v)}
      />
      <Field
        label={rateLabel}
        value={line[rateField]}
        step={rateStep}
        pct={ratePct}
        decimals={ratePct ? 3 : undefined}
        onChange={(v) => setLine(rateField, v)}
      />
    </div>
  );
}
